import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { Warning, CheckCircle, CalendarBlank } from "@phosphor-icons/react";
import { fmtDate } from "../../lib/dateUtil";
import DayPlanTimeline from "./DayPlanTimeline";
import SubJobTimeDialog from "./SubJobTimeDialog";

const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const dayName = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" });

/* Same-day crew plan: every sub-job at the property by visit date, with clash warnings and quick reschedule */
export default function SubJobsDayPlan({ parentId, refreshKey, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [date, setDate] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    API.get(`/jobs/${parentId}/sub-jobs/day-plan`).then(({ data }) => {
      setPlan(data);
      setDate((d) => (d && data.days.some((x) => x.date === d) ? d : data.days[0]?.date || null));
    }).catch(() => setPlan({ days: [], unscheduled: [], clash_count: 0 }));
  }, [parentId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!plan) return <p className="px-4 py-3 text-xs text-muted-foreground">Loading day plan…</p>;
  const day = plan.days.find((d) => d.date === date);
  const saved = () => { load(); onChanged?.(); };

  return (
    <div className="border-t border-border" data-testid="sub-jobs-day-plan">
      <div className="px-4 pt-3 flex items-center gap-2 flex-wrap">
        {plan.days.map((d) => (
          <button key={d.date} type="button" onClick={() => setDate(d.date)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${d.date === date ? "bg-violet-600 text-white border-violet-600" : "border-border hover:border-violet-400"}`} data-testid={`day-plan-tab-${d.date}`}>
            {dayName(d.date)} {fmtDate(d.date)} <span className={`text-[9px] rounded-full px-1.5 ${d.date === date ? "bg-white/20" : "bg-muted"}`}>{d.items.length}</span>
            {d.clash_count > 0 && <Warning size={11} weight="fill" className={d.date === date ? "text-amber-200" : "text-amber-500"} />}
          </button>
        ))}
        {plan.days.length === 0 && <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><CalendarBlank size={13} /> No sub-jobs have a visit date yet — set one below to start planning the day.</p>}
        <span className={`ml-auto text-[11px] inline-flex items-center gap-1 ${plan.clash_count ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`} data-testid="day-plan-clash-total">
          {plan.clash_count ? <><Warning size={12} weight="fill" /> {plan.clash_count} clash{plan.clash_count === 1 ? "" : "es"} on site</> : plan.days.length ? <><CheckCircle size={12} weight="fill" /> No trade clashes</> : null}
        </span>
      </div>
      {day && (
        <div className="px-4 py-3 space-y-3">
          {day.clashes.length > 0 && (
            <ul className="text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 space-y-0.5" data-testid="day-plan-clash-list">
              {day.clashes.map((c, i) => <li key={i} className="flex items-center gap-1.5"><Warning size={11} weight="fill" className="shrink-0" /> {c.label} · {fmt(c.from)}–{fmt(c.to)} — different trades on site at the same time</li>)}
            </ul>
          )}
          <DayPlanTimeline day={day} onPick={setEditing} />
        </div>
      )}
      {plan.unscheduled.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5" data-testid="day-plan-unscheduled">
          <span className="text-[10px] text-muted-foreground">Not yet scheduled:</span>
          {plan.unscheduled.map((i) => (
            <button key={i.id} type="button" onClick={() => setEditing(i)} className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-border hover:border-primary hover:text-primary" data-testid={`day-plan-unscheduled-${i.id}`}>
              {i.sub_job_index ? `${i.sub_job_index}. ` : ""}{i.title} · {i.assignee || "Unassigned"} — set a date
            </button>
          ))}
        </div>
      )}
      {editing && <SubJobTimeDialog item={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={saved} />}
    </div>
  );
}
